import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IdCard,
  Database,
  SlidersHorizontal,
  Users,
  Send,
  Sparkles,
  Mail,
  TrendingUp,
  AlertTriangle,
  DollarSign,
  Crown,
  Activity,
  ArrowRight,
  Filter,
  MoreVertical,
  RefreshCw,
  Search,
  SquarePen,
  Trash2,
  UserPlus,
  Workflow,
} from "lucide-react";
import { toast } from "sonner";
import { TraitCategory, TraitRow } from "../data";
import {
  CATEGORY_FROM_API,
  CATEGORY_TO_API,
  TraitApiCategory,
  TraitDefinition,
  TraitStatus,
  TraitUsage,
  formatCoverage,
  formatSampleValue,
  formatTimestamp,
  useTraitsList,
  useTraitsOverview,
} from "../api";
import { useWarehouseCatalog, useWarehouseStatus, formatWarehouseTimestamp, formatWarehouseValue } from "../warehouse-api";
import TraitDetailDrawer from "./trait-detail-drawer";

// Traits dashboard — wired to the traits mock API (docs/traits-mock-api.md,
// docs/traits-mock-ui-integration.md). Landing content (categories, recent
// traits, usage cards) comes from one GET /traits/overview; searching,
// filtering or "Show All" switches to the paginated GET /traits catalog. The
// API has no create/edit/delete endpoint yet, so those controls stay local,
// clearly-marked demo actions — see the trait builder (../new-trait).

const PER_PAGE = 10;

const HOW_IT_WORKS = [
  { icon: Database, title: "Choose Trait Type", body: "Pick a custom, computed or predictive trait based on your use case." },
  { icon: SlidersHorizontal, title: "Define Logic", body: "Map a field from a source, set a formula or configure a prediction rule" },
  { icon: Users, title: "Preview Trait Values", body: "Review sample values, coverage and quality before saving" },
  { icon: Send, title: "Use across ConvoHub", body: "Apply traits in segments, journeys, campaigns and profile views" },
];

const CATEGORY_ICON: Record<TraitCategory, React.ComponentType<any>> = {
  Custom: Database,
  Behavioral: IdCard,
  Predictive: Sparkles,
};

const CATEGORY_CHIP: Record<TraitCategory, string> = {
  Custom: "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100",
  Behavioral: "bg-green-300 dark:bg-green-500/40 text-gray-900 dark:text-gray-100",
  Predictive: "bg-yellow-300 dark:bg-yellow-500/40 text-gray-900 dark:text-gray-100",
};

const CATEGORY_CTA: Record<TraitCategory, string> = {
  Custom: "New Custom Trait",
  Behavioral: "New Computed Trait",
  Predictive: "New Predictive Trait",
};

const USAGE_ICON: Record<TraitUsage["key"], React.ComponentType<any>> = {
  segments: Database,
  journeys: Workflow,
  profile_views: UserPlus,
};

// Fallback only for while /traits/overview is loading or if it errors —
// real usage cards come from overview.usage once it loads.
const USED_IN_FALLBACK: TraitUsage[] = [
  { key: "segments", label: "Segments", description: "Use traits to build targeted customer segments (e.g. High value, At risk, Salary customers)" },
  { key: "journeys", label: "Journeys", description: "Trigger personalised journey based on trait values and customer behaviour" },
  { key: "profile_views", label: "Profile Views", description: "See trait values in customer profiles for a complete 360 degree view" },
];

const friendlyDataType = (v: string) => v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

interface DisplayRow {
  key: string;
  name: string;
  category: TraitCategory;
  dataType: string;
  sample: string;
  coverage: number;
  updated: string;
  status: TraitStatus;
}

const toDisplayRow = (t: TraitDefinition): DisplayRow => ({
  key: t.key,
  name: t.name || t.key,
  category: CATEGORY_FROM_API[t.category] ?? "Custom",
  dataType: friendlyDataType(t.data_type || "—"),
  sample: formatSampleValue(t.sample_value),
  coverage: t.coverage,
  updated: formatTimestamp(t.last_updated),
  status: t.status,
});

// The trait builder is a local-only demo (createConfig only reads
// name/type/sample) — status is never shown there, so "Active" is a safe
// constant regardless of the real trait's status.
const toBuilderRow = (row: DisplayRow): TraitRow => ({
  name: row.name,
  category: row.category,
  type: row.dataType,
  sample: row.sample,
  coverage: row.coverage,
  updated: row.updated,
  status: "Active",
});

// ─── Animated illustration ──────────────────────────────────────────────────
// Central profile card with trait chips orbiting it — chips drift on their
// own staggered cycles, the connector lines flow, and the anchor dots
// pulse. Everything is CSS keyframes so it stays smooth and cheap, and it
// all pauses under prefers-reduced-motion.

const CHIPS: { icon: React.ComponentType<any>; label: string; value: string; pos: string; delay: string; tone: string }[] = [
  { icon: Activity, label: "Lifecycle stage", value: "Active Customer", pos: "top-2 left-0", delay: "0s", tone: "text-emerald-600" },
  { icon: AlertTriangle, label: "Churn risk", value: "Low", pos: "top-2 right-0", delay: "0.6s", tone: "text-rose-500" },
  { icon: Mail, label: "Preferred channel", value: "Email", pos: "top-1/2 -translate-y-1/2 left-0", delay: "1.1s", tone: "text-sky-600" },
  { icon: DollarSign, label: "Total lifetime spend", value: "$2,840", pos: "top-1/2 -translate-y-1/2 right-0", delay: "0.3s", tone: "text-amber-600" },
  { icon: TrendingUp, label: "Purchase propensity", value: "78%", pos: "bottom-2 left-2", delay: "0.9s", tone: "text-indigo-600" },
  { icon: Crown, label: "Plan tier", value: "Pro", pos: "bottom-2 right-2", delay: "0.45s", tone: "text-fuchsia-600" },
];

const TraitIllustration: React.FC = () => (
  <div className="relative w-full h-[300px] select-none" aria-hidden="true">
    <style>{`
      @keyframes traitFloat {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
      }
      @keyframes traitFlow { to { stroke-dashoffset: -24; } }
      @keyframes traitPulse {
        0%, 100% { opacity: 0.35; transform: scale(1); }
        50% { opacity: 1; transform: scale(1.6); }
      }
      .trait-chip { animation: traitFloat 5s ease-in-out infinite; }
      .trait-line { stroke-dasharray: 4 6; animation: traitFlow 1.6s linear infinite; }
      .trait-dot { animation: traitPulse 2.4s ease-in-out infinite; transform-origin: center; }
      @media (prefers-reduced-motion: reduce) {
        .trait-chip, .trait-line, .trait-dot { animation: none !important; }
      }
    `}</style>

    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 320 300" preserveAspectRatio="none">
      {[
        [40, 44],
        [280, 44],
        [24, 150],
        [296, 150],
        [56, 256],
        [264, 256],
      ].map(([x, y], i) => (
        <g key={i}>
          <line className="trait-line" x1={160} y1={150} x2={x} y2={y} stroke="#c7d2fe" strokeWidth={1.5} />
          <circle className="trait-dot" cx={x} cy={y} r={2.5} fill="#6366f1" style={{ animationDelay: `${i * 0.3}s` }} />
        </g>
      ))}
      <circle cx={160} cy={150} r={4} fill="#4f46e5" />
    </svg>

    {/* Center profile card */}
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[150px] bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-lg p-3 text-center z-10">
      <div className="w-11 h-11 mx-auto rounded-full bg-gradient-to-tr from-indigo-400 to-fuchsia-400 flex items-center justify-center mb-1.5">
        <span className="text-white text-sm font-bold">TK</span>
      </div>
      <p className="text-xs font-bold text-gray-900 dark:text-gray-100">Taylor Kim</p>
      <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate">taylor.kim@example.com</p>
    </div>

    {/* Orbiting chips */}
    {CHIPS.map((chip) => {
      const Icon = chip.icon;
      return (
        <div
          key={chip.label}
          className={`trait-chip absolute ${chip.pos} bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm px-2.5 py-1.5 flex items-center gap-1.5 max-w-[140px]`}
          style={{ animationDelay: chip.delay }}
        >
          <Icon size={13} className={`${chip.tone} shrink-0`} />
          <div className="min-w-0">
            <p className="text-[9px] text-gray-400 dark:text-gray-500 leading-tight truncate">{chip.label}</p>
            <p className="text-[10px] font-semibold text-gray-800 dark:text-gray-100 leading-tight truncate">{chip.value}</p>
          </div>
        </div>
      );
    })}
  </div>
);

// ─── Page ───────────────────────────────────────────────────────────────────

const DemoBadge: React.FC<{ className?: string }> = ({ className }) => (
  <span
    title="This action isn't backed by a real API yet — nothing is saved on the server."
    className={`text-[10px] uppercase tracking-wide font-semibold bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded ${className || ""}`}
  >
    Demo
  </span>
);

const RowMenu: React.FC<{ onView: () => void; onEdit: () => void; onDelete: () => void }> = ({ onView, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Portaled to <body> and positioned ABOVE the trigger, fixed to the
  // viewport — the traits table sits inside an `overflow-x-auto` wrapper
  // (which also clips vertically), so an in-flow absolute menu was getting
  // cut off for rows near the bottom, only visible after scrolling.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ bottom: number; right: number } | null>(null);

  const updateCoords = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setCoords({ bottom: window.innerHeight - rect.top + 6, right: window.innerWidth - rect.right });
  };

  useLayoutEffect(() => {
    if (open) updateCoords();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScrollResize = () => updateCoords();
    window.addEventListener("scroll", onScrollResize, true);
    window.addEventListener("resize", onScrollResize);
    return () => {
      window.removeEventListener("scroll", onScrollResize, true);
      window.removeEventListener("resize", onScrollResize);
    };
  }, [open]);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const item = "w-full flex items-center gap-3 px-5 py-3 text-left text-base hover:bg-gray-50 dark:hover:bg-gray-800";
  return (
    <div className="relative inline-block" ref={ref}>
      <button ref={triggerRef} type="button" onClick={() => setOpen((o) => !o)} title="Actions" className="p-1.5 text-indigo-700 dark:text-indigo-400">
        <MoreVertical size={20} />
      </button>
      {open &&
        coords &&
        createPortal(
          <div
            ref={menuRef}
            style={{ position: "fixed", bottom: coords.bottom, right: coords.right }}
            className="z-[80] w-56 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700 rounded-xl shadow-lg divide-y divide-indigo-100 dark:divide-gray-800 overflow-hidden"
          >
            <button type="button" className={`${item} text-gray-800 dark:text-gray-100`} onClick={() => { setOpen(false); onView(); }}>
              <IdCard size={22} /> View Details
            </button>
            <button type="button" className={`${item} text-gray-800 dark:text-gray-100 justify-between`} onClick={() => { setOpen(false); onEdit(); }}>
              <span className="flex items-center gap-3"><SquarePen size={22} /> Edit Trait
              </span>
               {/* <DemoBadge /> */}
            </button>
            <button type="button" className={`${item} text-gray-400 dark:text-gray-500 justify-between`} onClick={() => { setOpen(false); onDelete(); }} title="Deleting traits isn't supported by the API yet">
              <span className="flex items-center gap-3"><Trash2 size={22} /> Delete Trait</span>
               {/* <DemoBadge /> */}
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};

const CardSkeleton: React.FC = () => (
  <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 h-52 animate-pulse">
    <div className="h-5 w-2/3 bg-gray-100 dark:bg-gray-800 rounded mb-4" />
    <div className="h-8 w-12 bg-gray-100 dark:bg-gray-800 rounded mb-3" />
    <div className="h-4 w-full bg-gray-100 dark:bg-gray-800 rounded mb-2" />
    <div className="h-4 w-4/5 bg-gray-100 dark:bg-gray-800 rounded" />
  </div>
);

const RowSkeleton: React.FC = () => (
  <tr className="border-b border-gray-50 dark:border-gray-800 last:border-0">
    <td colSpan={8} className="px-5 py-5">
      <div className="h-4 w-full bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
    </td>
  </tr>
);

const AllTraits: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Same page is mounted under both the CRM (/traits) and Unify
  // (/unify/traits/all) layouts — the builder lives beside whichever is active.
  const builderPath = pathname.startsWith("/unify") ? "/unify/traits/new" : "/traits/new";

  const { data: overview, loading: overviewLoading, error: overviewError, retry: retryOverview } = useTraitsOverview();

  // ── Live Data (warehouse) mode ───────────────────────────────────────────
  // See docs/data-warehouse-traits-integration.md. Defaults to on as soon as
  // the warehouse reports available — the mock catalog above is kept only as
  // a manual fallback (via the toggle), not the default experience.
  const { data: warehouseStatus } = useWarehouseStatus();
  const warehouseAvailable = !!(warehouseStatus?.enabled && warehouseStatus?.configured);
  const [liveMode, setLiveMode] = useState(false);
  const [liveModeInitialized, setLiveModeInitialized] = useState(false);
  useEffect(() => {
    if (!liveModeInitialized && warehouseAvailable) {
      setLiveMode(true);
      setLiveModeInitialized(true);
    }
  }, [warehouseAvailable, liveModeInitialized]);
  const { data: warehouseCatalog, loading: warehouseLoading, error: warehouseError } = useWarehouseCatalog(liveMode);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [category, setCategory] = useState<TraitCategory | "All">("All");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const filtersRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(e.target as Node)) setFiltersOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  // Debounce search ~300ms; reset to page 1 whenever the search term or
  // category changes so a new filter never lands on a stale page.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, category]);

  // Recent-traits (from the overview) is the default view; searching,
  // filtering by category, or asking to "Show All" switches to the real
  // paginated catalog endpoint.
  const catalogMode = showAll || debouncedSearch.trim() !== "" || category !== "All";
  const apiCategory: TraitApiCategory | undefined = category === "All" ? undefined : CATEGORY_TO_API[category];
  const { data: catalog, loading: catalogLoading, error: catalogError, retry: retryCatalog } = useTraitsList(
    catalogMode ? { category: apiCategory, search: debouncedSearch, page, perPage: PER_PAGE } : null
  );

  const recentRows = useMemo(() => (overview?.recent_traits || []).map(toDisplayRow), [overview]);
  const catalogRows = useMemo(() => (catalog?.data || []).map(toDisplayRow), [catalog]);
  const visible = catalogMode ? catalogRows : recentRows;
  const totalTraitCount = overview?.categories.reduce((sum, c) => sum + c.count, 0) ?? 0;
  const showAllOnly = showAll && !debouncedSearch.trim() && category === "All";

  const create = (cat?: TraitCategory) => navigate(builderPath, { state: cat ? { category: cat } : undefined });
  const openDetails = (row: DisplayRow) => setSelectedKey(row.key);
  const openEdit = (row: DisplayRow) => navigate(builderPath, { state: { category: row.category, trait: toBuilderRow(row), step: 2 } });
  const remove = () => toast.info("Deleting traits isn't supported by the API yet — this is a read-only demo.");

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
      <div className="mx-auto space-y-6">
        <div className="flex items-center gap-2 border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 rounded-full px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 w-fit">
          <IdCard size={15} className="text-gray-400" />
          Turn raw customer data into reusable traits
        </div>

        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Traits</h1>
              {/* <DemoBadge title="Trait data comes from a mock API for now — creating, editing and deleting aren't persisted." /> */}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-4xl">
              Traits are customer attributes created from connected data, calculations or predictive models, Use traits to
              understand customers better, build smarter segments and activate journeys with more contexts.
            </p>
          </div>
          {warehouseAvailable && (
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Live Data</span>
              <span className="text-[9px] uppercase font-bold text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/15 px-1 py-0.5 rounded">Beta</span>
              <button
                type="button"
                role="switch"
                aria-checked={liveMode}
                onClick={() => setLiveMode((v) => !v)}
                title="Toggle the real trait catalog from the connected data warehouse, scoped to your organization."
                className={`relative w-9 h-5 rounded-full transition-colors ${liveMode ? "bg-indigo-600" : "bg-gray-300 dark:bg-gray-600"}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${liveMode ? "translate-x-4" : ""}`} />
              </button>
            </label>
          )}
        </div>

        {/* Hero / how it works */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-5 sm:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-6">
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
                {HOW_IT_WORKS.map((step, i) => {
                  const Icon = step.icon;
                  return (
                    <div key={step.title} className={`text-center ${i > 0 ? "xl:border-l xl:border-gray-100 xl:dark:border-gray-800 xl:pl-5" : ""}`}>
                      <Icon size={22} className="text-indigo-700 mb-3 mx-auto" />
                      <p className="text-base text-gray-900 dark:text-gray-100 mb-1.5">{step.title}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{step.body}</p>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => create()}
                className="mt-6 inline-flex items-center gap-2 px-5 py-3 text-base text-white bg-indigo-700 hover:bg-indigo-800 rounded-lg shadow-sm transition-colors"
              >
                Create New Trait 
                {/* <DemoBadge className="bg-white/20 text-white" /> */}
              </button>
            </div>

            <div className="hidden lg:block">
              <TraitIllustration />
            </div>
          </div>
        </div>

        {liveMode ? (
          /* Warehouse catalog — flat list, no category/pagination/search on
             this endpoint (docs/data-warehouse-traits-integration.md). */
          <div>
            <div className="mb-4">
              <h2 className="text-2xl text-gray-900 dark:text-gray-100">Trait Catalog</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">Live definitions from the connected data warehouse</p>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800">
              {warehouseError ? (
                <div className="px-5 py-10 text-center">
                  <p className="text-sm text-rose-600 dark:text-rose-400">{warehouseError}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[900px] text-base">
                    <thead>
                      <tr className="text-left text-gray-900 dark:text-gray-100 border-b border-gray-100 dark:border-gray-800">
                        {["Trait Name", "Type", "Data Type", "Example Value", "Profiles", "Coverage", "Last Computed"].map((h) => (
                          <th key={h} className="px-5 py-4 font-normal">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {warehouseLoading ? (
                        Array.from({ length: 6 }).map((_, i) => <RowSkeleton key={i} />)
                      ) : (warehouseCatalog?.data || []).length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-5 py-10 text-center text-sm text-gray-400">
                            No trait definitions in the warehouse catalog yet.
                          </td>
                        </tr>
                      ) : (
                        (warehouseCatalog?.data || []).map((t) => (
                          <tr key={t.trait_name} className="border-b border-gray-50 dark:border-gray-800 last:border-0">
                            <td className="px-5 py-5 text-gray-900 dark:text-gray-100 max-w-[180px] truncate" title={t.trait_name}>
                              {t.trait_name}
                            </td>
                            <td className="px-5 py-5">
                              <span className="inline-flex items-center gap-1.5 text-sm px-2 py-1 rounded bg-indigo-50 dark:bg-indigo-500/15 text-gray-700 dark:text-gray-200">
                                {t.warehouse_trait_type || t.trait_type}
                              </span>
                            </td>
                            <td className="px-5 py-5 text-gray-900 dark:text-gray-100">{friendlyDataType(t.data_type || "—")}</td>
                            <td className="px-5 py-5 text-gray-900 dark:text-gray-100 max-w-[220px] truncate" title={formatWarehouseValue(t.example_value)}>
                              {formatWarehouseValue(t.example_value)}
                            </td>
                            <td className="px-5 py-5 text-gray-900 dark:text-gray-100">{t.profile_count.toLocaleString()}</td>
                            <td className="px-5 py-5">
                              <div className="w-14">
                                <p className="text-sm text-gray-900 dark:text-gray-100">{t.coverage.toFixed(1)}%</p>
                                <div className="h-1 rounded-full bg-indigo-100 dark:bg-gray-800 overflow-hidden mt-1">
                                  <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.max(t.coverage / 4, 4)}%` }} />
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-5 text-gray-900 dark:text-gray-100 whitespace-nowrap">{formatWarehouseTimestamp(t.last_computed_at)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : (
        <>
        {/* Category cards */}
        {overviewError ? (
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-rose-200 dark:border-rose-500/30 p-6 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-sm text-rose-600 dark:text-rose-400">{overviewError}</p>
            <button type="button" onClick={retryOverview} className="inline-flex items-center gap-2 text-sm text-indigo-700 dark:text-indigo-400 underline underline-offset-2">
              <RefreshCw size={16} /> Retry
            </button>
          </div>
        ) : overviewLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(overview?.categories || []).map((cat) => {
              const uiCategory = CATEGORY_FROM_API[cat.category];
              const Icon = CATEGORY_ICON[uiCategory];
              return (
                <div key={cat.category} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 flex flex-col">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon size={20} className="text-indigo-700" />
                    <span className="text-lg text-gray-900 dark:text-gray-100">{cat.label}</span>
                  </div>
                  <p className="text-2xl text-gray-900 dark:text-gray-100 mb-2">{cat.count}</p>
                  <p className="text-base text-gray-700 dark:text-gray-300 mb-3">{cat.description}</p>
                  <div className="flex flex-wrap gap-2 mb-5 flex-1 content-start">
                    {cat.trait_keys.slice(0, 4).map((t, i) => (
                      <span key={`${t}-${i}`} className="text-sm px-2.5 py-1 rounded bg-indigo-50 dark:bg-indigo-500/15 text-gray-700 dark:text-gray-200">
                        {t}
                      </span>
                    ))}
                  </div>
                  <button type="button" onClick={() => create(uiCategory)} className="inline-flex items-center gap-2 text-base text-indigo-700 dark:text-indigo-400 underline underline-offset-2 self-start">
                    {CATEGORY_CTA[uiCategory]} <ArrowRight size={18} /> 
                    {/* <DemoBadge /> */}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Recent / catalog traits */}
        <div>
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div>
              <h2 className="text-2xl text-gray-900 dark:text-gray-100">{catalogMode ? "All Traits" : "Recent Traits"}</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{catalogMode ? "Search the full trait catalog" : "Recently added traits"}</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative w-64 sm:w-80">
                <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-700 dark:text-gray-300" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search Traits"
                  maxLength={100}
                  className="w-full pl-10 pr-3 py-3 text-base rounded-lg border border-indigo-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
              </div>
              <div className="relative" ref={filtersRef}>
                <button
                  type="button"
                  onClick={() => setFiltersOpen((o) => !o)}
                  className="flex items-center gap-2 px-4 py-3 text-base rounded-lg border border-indigo-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                >
                  <Filter size={20} /> Filters{category !== "All" && <span className="w-2 h-2 rounded-full bg-indigo-700" />}
                </button>
                {filtersOpen && (
                  <div className="absolute right-0 z-20 mt-1 w-48 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700 rounded-xl shadow-lg py-1">
                    <p className="px-4 py-2 text-xs text-gray-500">Category</p>
                    {(["All", "Custom", "Behavioral", "Predictive"] as const).map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => {
                          setCategory(c);
                          setFiltersOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 ${category === c ? "text-indigo-700 font-semibold" : "text-gray-800 dark:text-gray-100"}`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800">
            {catalogMode && catalogError ? (
              <div className="px-5 py-10 text-center">
                <p className="text-sm text-rose-600 dark:text-rose-400 mb-3">{catalogError}</p>
                <button type="button" onClick={retryCatalog} className="inline-flex items-center gap-2 text-sm text-indigo-700 dark:text-indigo-400 underline underline-offset-2">
                  <RefreshCw size={16} /> Retry
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[980px] text-base">
                  <thead>
                    <tr className="text-left text-gray-900 dark:text-gray-100 border-b border-gray-100 dark:border-gray-800">
                      {["Trait Name", "Category", "Type", "Sample Value", "Coverage", "Last Updated", "Status", "Actions"].map((h) => (
                        <th key={h} className="px-5 py-4 font-normal">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(overviewLoading && !catalogMode) || (catalogMode && catalogLoading) ? (
                      Array.from({ length: 6 }).map((_, i) => <RowSkeleton key={i} />)
                    ) : (
                      <>
                        {visible.map((t) => (
                          <tr key={t.key} className="border-b border-gray-50 dark:border-gray-800 last:border-0">
                            <td className="px-5 py-5 text-gray-900 dark:text-gray-100 max-w-[180px] truncate" title={t.name}>{t.name}</td>
                            <td className="px-5 py-5">
                              <span className={`inline-flex items-center gap-1.5 text-sm px-2 py-1 rounded ${CATEGORY_CHIP[t.category]}`}>
                                <span className="w-1.5 h-1.5 rounded-full bg-gray-900 dark:bg-gray-100" />
                                {t.category}
                              </span>
                            </td>
                            <td className="px-5 py-5 text-gray-900 dark:text-gray-100">{t.dataType}</td>
                            <td className="px-5 py-5 text-gray-900 dark:text-gray-100 max-w-[220px] truncate" title={t.sample}>{t.sample}</td>
                            <td className="px-5 py-5">
                              <div className="w-14">
                                <p className="text-sm text-gray-900 dark:text-gray-100">{formatCoverage(t.coverage)}</p>
                                <div className="h-1 rounded-full bg-indigo-100 dark:bg-gray-800 overflow-hidden mt-1">
                                  <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.max(t.coverage / 4, 4)}%` }} />
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-5 text-gray-900 dark:text-gray-100 whitespace-nowrap">{t.updated}</td>
                            <td className="px-5 py-5">
                              <span className={`inline-flex items-center gap-1.5 text-sm px-2 py-1 rounded ${t.status === "active" ? "bg-green-100 dark:bg-green-500/15 text-gray-800 dark:text-green-300" : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200"}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${t.status === "active" ? "bg-green-500" : "bg-gray-400"}`} /> {t.status === "active" ? "Active" : "Draft"}
                              </span>
                            </td>
                            <td className="px-5 py-5">
                              <RowMenu onView={() => openDetails(t)} onEdit={() => openEdit(t)} onDelete={remove} />
                            </td>
                          </tr>
                        ))}
                        {visible.length === 0 && (
                          <tr>
                            <td colSpan={8} className="px-5 py-10 text-center text-sm text-gray-400">
                              No traits match.
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
            {catalogMode && catalog && catalog.total_pages > 1 && (
              <>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Page {catalog.page} of {catalog.total_pages} · {catalog.total} traits
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={page >= catalog.total_pages}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </>
            )}
            {!catalogMode && !overviewLoading && totalTraitCount > recentRows.length && (
              <button type="button" onClick={() => setShowAll(true)} className="ml-auto text-base text-indigo-700 dark:text-indigo-400 underline underline-offset-2">
                Show All
              </button>
            )}
            {showAllOnly && (
              <button type="button" onClick={() => setShowAll(false)} className="ml-auto text-base text-indigo-700 dark:text-indigo-400 underline underline-offset-2">
                Show Less
              </button>
            )}
          </div>
        </div>
        </>
        )}

        {/* How traits are used */}
        <div>
          <h2 className="text-2xl text-gray-900 dark:text-gray-100">How Traits are used</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 mb-4">
            Traits power multiple features across ConvoHub to help you understand and engage your customers
          </p>
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-5">
            <div className="grid grid-cols-1 md:grid-cols-3">
              {(overview?.usage?.length ? overview.usage : USED_IN_FALLBACK).map((u, i) => {
                const Icon = USAGE_ICON[u.key];
                return (
                  <div key={u.key} className={`text-center px-6 py-4 ${i > 0 ? "md:border-l md:border-gray-100 md:dark:border-gray-800" : ""}`}>
                    <Icon size={22} className="text-indigo-700 mx-auto mb-3" />
                    <p className="text-lg text-gray-900 dark:text-gray-100 mb-1.5">{u.label}</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{u.description}</p>
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => create()}
              className="mt-5 inline-flex items-center gap-2 px-5 py-3 text-base text-white bg-indigo-700 hover:bg-indigo-800 rounded-lg shadow-sm transition-colors"
            >
              Create New Trait 
              {/* <DemoBadge className="bg-white/20 text-white" /> */}
            </button>
          </div>
        </div>
      </div>

      <TraitDetailDrawer traitKey={selectedKey} onClose={() => setSelectedKey(null)} />
    </div>
  );
};

export default AllTraits;
